import { useEffect, type RefObject } from "react";

/**
 * Переписка занимает ровно видимую часть экрана, и клавиатура не закрывает поле ввода.
 *
 * На iPhone клавиатура не уменьшает окно (100dvh остаётся прежним), а сдвигает «видимый
 * вьюпорт» (visualViewport): его высота — то, что осталось над клавиатурой, offsetTop —
 * на сколько Safari прокрутил страницу, чтобы показать поле. Экран переписки стоит
 * position: fixed и берёт размеры из этих двух чисел — поле ввода всегда над клавиатурой.
 * На Android Chrome то же даёт interactive-widget=resizes-content в meta viewport, а
 * visualViewport просто совпадает с окном.
 *
 * Пока экран открыт, сама страница не прокручивается (html.chat-lock): прокрутка — только
 * у ленты, иначе на iPhone вся переписка «уезжает» вместе с полем ввода.
 */

/** Клавиатура открыта, если видимая часть ниже окна больше чем на столько. */
const KEYBOARD_THRESHOLD_PX = 120;

export function useChatViewport(root: RefObject<HTMLElement>, onResize?: () => void): void {
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const html = document.documentElement;
    html.classList.add("chat-lock");
    const viewport = window.visualViewport;

    let frame = 0;
    const apply = () => {
      frame = 0;
      const height = viewport?.height ?? window.innerHeight;
      const top = viewport?.offsetTop ?? 0;
      element.style.setProperty("--chat-height", `${Math.round(height)}px`);
      element.style.setProperty("--chat-top", `${Math.round(top)}px`);
      element.classList.toggle("chat--keyboard", window.innerHeight - height > KEYBOARD_THRESHOLD_PX);
      // Safari прокрутил страницу к полю — возвращаем: экран и так стоит над клавиатурой
      if (!viewport && window.scrollY !== 0) window.scrollTo(0, 0);
      onResize?.();
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    apply();
    viewport?.addEventListener("resize", schedule);
    viewport?.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      html.classList.remove("chat-lock");
      element.style.removeProperty("--chat-height");
      element.style.removeProperty("--chat-top");
    };
    // onResize — из ref вызывающего; перезапуск на каждую перерисовку не нужен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [root]);
}
